const CYCLE_MS = 72_000
const CLOUD_HEIGHT = 36
const LOOP_DEPTH = 288
const NEAR_FIELD_DEPTH_WARP = 16
const TAU = Math.PI * 2

function visibleDepthForFlowDepth(flowDepth) {
  return flowDepth - NEAR_FIELD_DEPTH_WARP * Math.sin(TAU * flowDepth / LOOP_DEPTH)
}

function flowDepthForVisibleDepth(visibleDepth) {
  let flowDepth = visibleDepth

  // Invert the monotonic depth warp so the painted composition remains unchanged at rest.
  for (let iteration = 0; iteration < 6; iteration += 1) {
    const angle = TAU * flowDepth / LOOP_DEPTH
    const error = visibleDepthForFlowDepth(flowDepth) - visibleDepth
    const slope = 1 - NEAR_FIELD_DEPTH_WARP * TAU / LOOP_DEPTH * Math.cos(angle)
    flowDepth -= error / slope
  }

  return flowDepth
}

// The anchors are the inverse spherical projection of the painted panorama's
// cloud centres. Three toroidal copies make one continuous weather plane.
const CLOUD_FOOTPRINTS = [
  { x: 23, z: 41, width: 71, length: 26, atlas: 0, relief: 4.8 },
  { x: -45, z: 17, width: 51, length: 20, atlas: 1, relief: 1.2 },
  { x: 40, z: 3, width: 22, length: 10, atlas: 2, relief: 1.0 },
  { x: -66, z: 127, width: 142, length: 25, atlas: 3, relief: 4.0 },
  { x: 82, z: 76, width: 78, length: 22, atlas: 4, relief: 4.2 },
  { x: 13, z: 60, width: 53, length: 20, atlas: 5, relief: 3.8 },
  { x: 186, z: 216, width: 95, length: 24, atlas: 13, relief: 4.2 },
  { x: 20, z: 177, width: 82, length: 22, atlas: 15, relief: 3.8 },
  { x: -108, z: 200, width: 72, length: 20, atlas: 14, relief: 3.6 },
  { x: 17, z: 94, width: 58, length: 18, atlas: 14, relief: 3.4 },
  { x: 172, z: 129, width: 78, length: 22, atlas: 12, relief: 4.2 },
  { x: 27, z: 282, width: 90, length: 24, atlas: 12, relief: 4.4 },
  { x: 35, z: 110, width: 90, length: 22, atlas: 15, relief: 3.6 },
  { x: 170, z: 111, width: 72, length: 20, atlas: 12, relief: 4.0 },
  { x: 38, z: 153, width: 94, length: 24, atlas: 13, relief: 4.2 },
  { x: 175, z: 154, width: 74, length: 20, atlas: 14, relief: 3.4 },
  { x: 38, z: 238, width: 96, length: 24, atlas: 12, relief: 4.4 },
  { x: 178, z: 239, width: 82, length: 22, atlas: 15, relief: 3.8 },
  { x: 35, z: 260, width: 94, length: 24, atlas: 14, relief: 3.6 },
  { x: 160, z: 262, width: 78, length: 22, atlas: 13, relief: 4.0 },
  { x: -150, z: 30, width: 68, length: 16, atlas: 6, relief: 0.8 },
  { x: 155, z: 66, width: 72, length: 16, atlas: 7, relief: 0.6 },
  { x: -155, z: 102, width: 68, length: 16, atlas: 8, relief: 0.8 },
  { x: 160, z: 138, width: 48, length: 12, atlas: 9, relief: 0 },
  { x: -160, z: 174, width: 52, length: 12, atlas: 10, relief: 0 },
  { x: 155, z: 210, width: 70, length: 16, atlas: 6, relief: 0.8 },
  { x: -150, z: 246, width: 72, length: 16, atlas: 7, relief: 0.6 },
  { x: 150, z: 270, width: 64, length: 16, atlas: 8, relief: 0.8 },
  // A staggered centerline lane lets clouds enter from directly overhead.
  // These occupy longitudinal gaps in the painted plane to avoid crowding nearby anchors.
  { x: -2, z: 78, width: 42, length: 12, atlas: 15, relief: 2.4 },
  { x: 3, z: 132, width: 50, length: 16, atlas: 12, relief: 3.0 },
  { x: -1, z: 207, width: 64, length: 24, atlas: 0, relief: 3.8 },
]

const CLOUD_VERTEX = `
  precision highp float;

  attribute vec2 position;
  attribute vec4 aCloud;
  attribute float aAtlas;
  attribute float aRelief;

  uniform float uAspect;
  uniform float uPhase;

  varying vec2 vAtlasUv;
  varying float vVisibility;

  const float HALF_VERTICAL_FOV = 0.8;
  const float DESKTOP_CAMERA_PITCH = 0.5235987756;
  const float MOBILE_CAMERA_PITCH = 0.5061454831;
  const float LOOP_DEPTH = ${LOOP_DEPTH.toFixed(1)};
  const float NEAR_FIELD_DEPTH_WARP = ${NEAR_FIELD_DEPTH_WARP.toFixed(1)};
  const float TAU = 6.2831853072;

  float visibleDepthForFlowDepth(float flowDepth) {
    return flowDepth - NEAR_FIELD_DEPTH_WARP * sin(TAU * flowDepth / LOOP_DEPTH);
  }

  vec3 orientCloudDirection(vec3 world) {
    vec3 direction = normalize(world);

    float mobileMix = 1.0 - smoothstep(0.58, 0.95, uAspect);
    float cropShift = 1.0 - smoothstep(1.05, 2.05, uAspect);
    float cameraYaw = mobileMix * 0.08 + cropShift * 0.02;
    float yawCosine = cos(cameraYaw);
    float yawSine = sin(cameraYaw);
    vec3 yawed = vec3(
      yawCosine * direction.x - yawSine * direction.z,
      direction.y,
      yawSine * direction.x + yawCosine * direction.z
    );

    float cameraPitch = mix(DESKTOP_CAMERA_PITCH, MOBILE_CAMERA_PITCH, mobileMix);
    float pitchCosine = cos(cameraPitch);
    float pitchSine = sin(cameraPitch);
    return vec3(
      yawed.x,
      pitchCosine * yawed.y - pitchSine * yawed.z,
      pitchSine * yawed.y + pitchCosine * yawed.z
    );
  }

  vec2 projectCloudDirection(vec3 camera) {
    float halfHorizontalFov = atan(tan(HALF_VERTICAL_FOV) * uAspect);
    float azimuth = atan(camera.x, camera.z);
    float elevation = atan(camera.y, length(camera.xz));
    return vec2(azimuth / halfHorizontalFov, elevation / HALF_VERTICAL_FOV);
  }

  void main() {
    float flowDepth = aCloud.y + uPhase * LOOP_DEPTH;
    float centerDepth = visibleDepthForFlowDepth(flowDepth);
    float centerHeight = ${CLOUD_HEIGHT.toFixed(1)} + 0.25 * aRelief;
    vec3 anchorWorld = vec3(aCloud.x, centerHeight, centerDepth);
    vec3 anchorCamera = orientCloudDirection(anchorWorld);
    vec2 projectedCenter = projectCloudDirection(anchorCamera);

    float distanceToAnchor = max(length(anchorWorld), 1.0);
    float halfAngularSize = atan(0.5 * aCloud.z / distanceToAnchor);
    float halfHorizontalFov = atan(tan(HALF_VERTICAL_FOV) * uAspect);
    vec2 halfProjectedSize = vec2(
      halfAngularSize / halfHorizontalFov,
      halfAngularSize / HALF_VERTICAL_FOV
    );

    vec2 projected = projectedCenter + vec2(
      position.x * 2.0 * halfProjectedSize.x,
      position.y * 2.0 * halfProjectedSize.y
    );
    gl_Position = vec4(projected, 0.0, 1.0);

    float atlasColumn = mod(aAtlas, 4.0);
    float atlasRowFromTop = floor(aAtlas / 4.0);
    float textureRow = 3.0 - atlasRowFromTop;
    vec2 atlasSize = vec2(2048.0);
    vec2 cellSize = vec2(0.25);
    vec2 texelInset = vec2(2.0) / atlasSize;
    vec2 cellOrigin = vec2(atlasColumn * cellSize.x, textureRow * cellSize.y);
    vAtlasUv = cellOrigin + texelInset + (position + 0.5) * (cellSize - texelInset * 2.0);

    float frontVisibility = smoothstep(0.005, 0.025, anchorCamera.z);
    float distanceHaze = 1.0 - 0.40 * smoothstep(110.0, 235.0, centerDepth);
    float farVisibility = 1.0 - smoothstep(235.0, 284.0, centerDepth);
    vVisibility = frontVisibility * distanceHaze * farVisibility;
  }
`

const CLOUD_FRAGMENT = `
  precision highp float;

  uniform sampler2D tCloudAtlas;
  varying vec2 vAtlasUv;
  varying float vVisibility;

  void main() {
    vec4 cloud = texture2D(tCloudAtlas, vAtlasUv);
    float alpha = cloud.a * vVisibility;
    gl_FragColor = vec4(cloud.rgb * alpha, alpha);
  }
`

function createCloudGeometryData() {
  const positions = []
  const clouds = []
  const atlas = []
  const relief = []

  for (const copy of [-1, 0, 1]) {
    for (const footprint of CLOUD_FOOTPRINTS) {
      const flowDepth = flowDepthForVisibleDepth(footprint.z)
      const corners = [
        [0, 0], [1, 0], [0, 1],
        [0, 1], [1, 0], [1, 1],
      ]

      for (const [u, v] of corners) {
        positions.push(u - 0.5, v - 0.5)
        clouds.push(
          footprint.x,
          flowDepth + copy * LOOP_DEPTH,
          footprint.width,
          footprint.length,
        )
        atlas.push(footprint.atlas)
        relief.push(footprint.relief)
      }
    }
  }

  return {
    positions: new Float32Array(positions),
    clouds: new Float32Array(clouds),
    atlas: new Float32Array(atlas),
    relief: new Float32Array(relief),
  }
}

function observeMotion(media, reducedMotion) {
  let isIntersecting = true

  const sync = () => {
    const shouldRun = isIntersecting && !document.hidden && !reducedMotion.matches
    media.dataset.motion = shouldRun ? "running" : "paused"
  }

  const observer = new IntersectionObserver(([entry]) => {
    isIntersecting = entry.isIntersecting
    sync()
  }, { rootMargin: "0px" })

  observer.observe(media)
  document.addEventListener("visibilitychange", sync)
  reducedMotion.addEventListener("change", sync)
  sync()

  return () => {
    observer.disconnect()
    document.removeEventListener("visibilitychange", sync)
    reducedMotion.removeEventListener("change", sync)
  }
}

function observeForeground(media) {
  const foreground = media.querySelector(".pom-cloud-foreground")
  if (!foreground) {
    media.dataset.occluder = "failed"
    return () => {}
  }

  const ready = () => { media.dataset.occluder = "ready" }
  const failed = () => { media.dataset.occluder = "failed" }
  foreground.addEventListener("load", ready)
  foreground.addEventListener("error", failed)

  if (foreground.complete) {
    if (foreground.naturalWidth > 0) ready()
    else failed()
  }

  return () => {
    foreground.removeEventListener("load", ready)
    foreground.removeEventListener("error", failed)
  }
}

async function createCloudRenderer(media, canvas) {
  const { Geometry, Mesh, Program, Renderer, TextureLoader } = await import("ogl")
  let renderer

  try {
    renderer = new Renderer({
      canvas,
      alpha: true,
      antialias: false,
      depth: false,
      dpr: 1,
      premultipliedAlpha: true,
      powerPreference: "high-performance",
    })
  } catch {
    media.dataset.webgl = "failed"
    return () => {}
  }

  const gl = renderer.gl
  gl.clearColor(0, 0, 0, 0)

  const atlasTexture = TextureLoader.load(gl, {
    src: media.dataset.cloudAtlasUrl,
    generateMipmaps: true,
    minFilter: gl.LINEAR_MIPMAP_LINEAR,
    magFilter: gl.LINEAR,
    premultiplyAlpha: false,
  })
  const data = createCloudGeometryData()
  const geometry = new Geometry(gl, {
    position: { size: 2, data: data.positions },
    aCloud: { size: 4, data: data.clouds },
    aAtlas: { size: 1, data: data.atlas },
    aRelief: { size: 1, data: data.relief },
  })
  const program = new Program(gl, {
    vertex: CLOUD_VERTEX,
    fragment: CLOUD_FRAGMENT,
    uniforms: {
      uAspect: { value: 1 },
      uPhase: { value: 0 },
      tCloudAtlas: { value: atlasTexture },
    },
    transparent: true,
    cullFace: null,
    depthTest: false,
    depthWrite: false,
  })
  const cloudDeck = new Mesh(gl, { geometry, program, frustumCulled: false })

  try {
    await atlasTexture.loaded
  } catch {
    media.dataset.webgl = "failed"
    return () => {}
  }

  let animationFrame = 0
  let animationStart = 0
  let elapsedBeforePause = 0
  let isRunning = false

  const resize = () => {
    const viewWidth = Math.max(1, media.clientWidth)
    const viewHeight = Math.max(1, media.clientHeight)
    const dprCeiling = viewWidth <= 760 ? 1.35 : 1.5
    renderer.dpr = Math.min(window.devicePixelRatio, dprCeiling)
    renderer.setSize(viewWidth, viewHeight)
    program.uniforms.uAspect.value = viewWidth / viewHeight
  }

  const renderAt = (elapsedMs) => {
    program.uniforms.uPhase.value = (elapsedMs % CYCLE_MS) / CYCLE_MS
    renderer.render({ scene: cloudDeck, sort: false, frustumCull: false })
  }

  const frame = (now) => {
    if (!isRunning) return
    elapsedBeforePause = (now - animationStart) % CYCLE_MS
    renderAt(elapsedBeforePause)
    animationFrame = requestAnimationFrame(frame)
  }

  const start = () => {
    if (isRunning) return
    isRunning = true
    animationStart = performance.now() - elapsedBeforePause
    animationFrame = requestAnimationFrame(frame)
  }

  const pause = () => {
    if (!isRunning) return
    isRunning = false
    cancelAnimationFrame(animationFrame)
  }

  const syncMotion = () => {
    if (media.dataset.motion === "running") start()
    else pause()
  }

  const seek = (event) => {
    const requestedSeconds = Number(event.detail)
    if (!Number.isFinite(requestedSeconds)) return
    elapsedBeforePause = ((requestedSeconds * 1000) % CYCLE_MS + CYCLE_MS) % CYCLE_MS
    animationStart = performance.now() - elapsedBeforePause
    renderAt(elapsedBeforePause)
  }

  const loseContext = (event) => {
    event.preventDefault()
    media.dataset.webgl = "failed"
    pause()
  }

  const resizeObserver = new ResizeObserver(() => {
    resize()
    renderAt(elapsedBeforePause)
  })
  const motionObserver = new MutationObserver(syncMotion)

  canvas.addEventListener("cloud-dome-seek", seek)
  canvas.addEventListener("webglcontextlost", loseContext)
  resizeObserver.observe(media)
  motionObserver.observe(media, { attributes: true, attributeFilter: ["data-motion"] })

  resize()
  renderAt(0)
  canvas.dataset.ready = "true"
  media.dataset.webgl = "ready"
  syncMotion()

  return () => {
    pause()
    resizeObserver.disconnect()
    motionObserver.disconnect()
    canvas.removeEventListener("cloud-dome-seek", seek)
    canvas.removeEventListener("webglcontextlost", loseContext)
  }
}

function initializeCloudStudy(media) {
  const canvas = media.querySelector(".pom-cloud-canvas")
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
  const cleanups = [observeMotion(media, reducedMotion), observeForeground(media)]
  let rendererStarted = false
  let rendererDisposed = false
  let disposeRenderer = () => {}

  const startRenderer = async () => {
    if (rendererStarted || reducedMotion.matches || !canvas) return
    rendererStarted = true
    disposeRenderer = await createCloudRenderer(media, canvas)
    if (rendererDisposed) disposeRenderer()
  }

  const handleMotionPreference = () => { startRenderer() }
  reducedMotion.addEventListener("change", handleMotionPreference)
  cleanups.push(() => reducedMotion.removeEventListener("change", handleMotionPreference))
  startRenderer()

  return () => {
    rendererDisposed = true
    disposeRenderer()
    cleanups.forEach((cleanup) => cleanup())
  }
}

const cloudStudyCleanups = [...document.querySelectorAll("[data-pom-cloud-hero]")]
  .map(initializeCloudStudy)

window.addEventListener("pagehide", () => {
  cloudStudyCleanups.forEach((cleanup) => cleanup())
}, { once: true })
